import { Controller } from "@hotwired/stimulus"

// Sets account_id in a hidden field when user selects an account from search results (no navigation).
export default class extends Controller {
  static values = {
    url: { type: String },
    minLength: { type: Number, default: 2 },
    debounceMs: { type: Number, default: 250 },
    noResultsText: { type: String, default: "No accounts found." },
    searchingText: { type: String, default: "Searching…" },
    placeholder: { type: String, default: "Search by name, OIB or email…" }
  }

  static targets = ["input", "results", "accountId", "selectedLabel", "clearButton"]

  connect() {
    this.debounceTimer = null
    this.abortController = null
    this.boundHideResults = this.hideResults.bind(this)
    this.boundHandleKeydown = this.handleKeydown.bind(this)
  }

  disconnect() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.cancelFetch()
    document.removeEventListener("click", this.boundHideResults)
    if (this.hasInputTarget) this.inputTarget.removeEventListener("keydown", this.boundHandleKeydown)
  }

  input() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.performSearch(), this.debounceMsValue)
  }

  performSearch() {
    const q = this.inputTarget.value?.trim() || ""
    if (q.length < this.minLengthValue) {
      this.resultsTarget.hidden = true
      this.resultsTarget.innerHTML = ""
      return
    }

    this.cancelFetch()
    this.abortController = new AbortController()
    this.showLoading()

    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("q", q)

    fetch(url, {
      signal: this.abortController.signal,
      headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" }
    })
      .then(res => res.ok ? res.json() : [])
      .then(data => this.renderResults(data))
      .catch(() => { this.resultsTarget.hidden = true })
      .finally(() => { this.abortController = null })
  }

  showLoading() {
    this.resultsTarget.innerHTML = `
      <li class="flex items-center gap-2 p-3 text-base-content/70">
        <span class="loading loading-spinner loading-sm" aria-hidden="true"></span>
        <span>${this.escapeHtml(this.searchingTextValue)}</span>
      </li>
    `
    this.resultsTarget.hidden = false
  }

  renderResults(items) {
    if (!items.length) {
      this.resultsTarget.innerHTML = `
        <li class="px-4 py-3 text-base-content/60 text-sm" role="status">${this.escapeHtml(this.noResultsTextValue)}</li>
      `
    } else {
      this.resultsTarget.innerHTML = items.map(a => `
        <li class="border-b border-base-200 last:border-b-0">
          <button type="button" class="w-full text-left px-4 py-3 rounded-lg transition-colors hover:bg-primary/10 focus:bg-primary/10 focus:outline-none block"
            data-account-id="${this.escapeHtml(a.id)}"
            data-account-label="${this.escapeHtml(a.full_name)} (${this.escapeHtml(a.oib)})"
            data-action="click->account-select#select">
            <span class="font-medium text-base-content block">${this.escapeHtml(a.full_name)}</span>
            <span class="text-sm text-base-content/70 tabular-nums block mt-0.5">OIB: ${this.escapeHtml(a.oib)}</span>
          </button>
        </li>
      `).join("")
    }
    this.resultsTarget.hidden = false
    document.addEventListener("click", this.boundHideResults, { once: true, capture: true })
    this.inputTarget.addEventListener("keydown", this.boundHandleKeydown, { once: true })
  }

  select(e) {
    const btn = e.currentTarget
    const id = btn.dataset.accountId
    const label = btn.dataset.accountLabel
    if (this.hasAccountIdTarget) this.accountIdTarget.value = id
    if (this.hasSelectedLabelTarget) {
      this.selectedLabelTarget.textContent = label
      this.selectedLabelTarget.classList.remove("hidden")
    }
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
      this.inputTarget.hidden = true
    }
    if (this.hasClearButtonTarget) this.clearButtonTarget.classList.remove("hidden")
    this.resultsTarget.hidden = true
    this.resultsTarget.innerHTML = ""
  }

  clear(e) {
    e?.preventDefault()
    if (this.hasAccountIdTarget) this.accountIdTarget.value = ""
    if (this.hasSelectedLabelTarget) {
      this.selectedLabelTarget.textContent = ""
      this.selectedLabelTarget.classList.add("hidden")
    }
    if (this.hasInputTarget) {
      this.inputTarget.hidden = false
      this.inputTarget.classList.remove("hidden")
      this.inputTarget.value = ""
      this.inputTarget.focus()
    }
    if (this.hasClearButtonTarget) this.clearButtonTarget.classList.add("hidden")
  }

  hideResults(e) {
    if (!this.hasResultsTarget) return
    if (e?.type === "keydown") {
      if (e.key === "Escape") this.resultsTarget.hidden = true
      return
    }
    if (!this.element.contains(e?.target)) this.resultsTarget.hidden = true
  }

  handleKeydown(e) {
    if (e.key === "Escape") this.resultsTarget.hidden = true
  }

  cancelFetch() {
    if (this.abortController) this.abortController.abort()
  }

  escapeHtml(str) {
    if (str == null) return ""
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
